import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type ComboChannel = 'pdv' | 'table' | 'tablet' | 'delivery';

export interface ComboSlotProduct {
  id: string;
  name: string;
  price: number;
  image_url?: string | null;
}

export interface ComboSlotWithProducts {
  id: string;
  slot_type: 'category' | 'product';
  category_id: string | null;
  product_id: string | null;
  min_quantity: number;
  max_quantity: number;
  pricing_rule: 'highest' | 'average' | 'fixed' | 'rodizio';
  fixed_price: number | null;
  label: string | null;
  is_required: boolean;
  display_order: number;
  // Products available for selection (if slot_type = 'category')
  products?: ComboSlotProduct[];
  // Fixed product (if slot_type = 'product')
  product?: ComboSlotProduct;
  category?: { name: string };
}

// Public combo type - uses new combo_slots system
export interface ComboWithDetails {
  id: string;
  name: string;
  description: string | null;
  price: number;
  image_url: string | null;
  combo_type: string;
  is_active: boolean;
  category_id: string | null;
  restaurant_id: string;
  slots: ComboSlotWithProducts[];
}

// Alias for public use
export type PublicComboWithDetails = ComboWithDetails;

// Re-export legacy types for internal PDV use (from useCombos)
// These are NOT used by public pages
export type { Combo, ComboItem, ComboItemOption, ComboPizzaFlavor } from './useCombos';

export function usePublicCombos(restaurantId: string | undefined, channel: ComboChannel = 'pdv') {
  return useQuery({
    queryKey: ['public-combos', restaurantId, channel],
    queryFn: async () => {
      if (!restaurantId) return [];

      // Build the channel filter
      const channelField = `available_${channel}` as const;

      const { data: combos, error } = await supabase
        .from('combos')
        .select(`
          *,
          category:categories!combos_category_id_fkey(name)
        `)
        .eq('restaurant_id', restaurantId)
        .eq('is_active', true)
        .eq(channelField, true)
        .order('name');

      if (error) throw error;
      if (!combos || combos.length === 0) return [];

      // Fetch slots for each combo
      const combosWithDetails: ComboWithDetails[] = [];

      for (const combo of combos) {
        // Fetch slots for this combo
        const { data: slotsData, error: slotsError } = await supabase
          .from('combo_slots')
          .select(`
            *,
            category:categories(id, name),
            product:products(id, name, price, image_url)
          `)
          .eq('combo_id', combo.id)
          .order('display_order');

        if (slotsError) {
          console.error('Error fetching slots:', slotsError);
          continue;
        }

        const slots: ComboSlotWithProducts[] = [];

        for (const slot of slotsData || []) {
          const slotWithProducts: ComboSlotWithProducts = {
            id: slot.id,
            slot_type: slot.slot_type as 'category' | 'product',
            category_id: slot.category_id,
            product_id: slot.product_id,
            min_quantity: slot.min_quantity,
            max_quantity: slot.max_quantity,
            pricing_rule: slot.pricing_rule as 'highest' | 'average' | 'fixed' | 'rodizio',
            fixed_price: slot.fixed_price,
            label: slot.label,
            is_required: slot.is_required ?? true,
            display_order: slot.display_order ?? 0,
            category: slot.category ? { name: slot.category.name } : undefined,
            product: slot.product ? {
              id: slot.product.id,
              name: slot.product.name,
              price: Number(slot.product.price),
              image_url: slot.product.image_url,
            } : undefined,
          };

          // If slot_type is 'category', fetch all products from that category
          if (slot.slot_type === 'category' && slot.category_id) {
            const { data: productsData } = await supabase
              .from('products')
              .select('id, name, price, image_url')
              .eq('category_id', slot.category_id)
              .eq('is_active', true)
              .order('name');

            slotWithProducts.products = (productsData || []).map(p => ({
              id: p.id,
              name: p.name,
              price: Number(p.price),
              image_url: p.image_url,
            }));
          }

          slots.push(slotWithProducts);
        }

        combosWithDetails.push({
          id: combo.id,
          name: combo.name,
          description: combo.description,
          price: Number(combo.price),
          image_url: combo.image_url,
          combo_type: combo.combo_type,
          is_active: combo.is_active ?? true,
          category_id: combo.category_id,
          restaurant_id: combo.restaurant_id,
          slots,
        });
      }

      return combosWithDetails;
    },
    enabled: !!restaurantId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// Helper to calculate combo price based on slot selections
export function calculateComboPrice(
  combo: ComboWithDetails,
  slotSelections: Map<string, { productId: string; price: number }[]>
): number {
  let totalPrice = 0;

  for (const slot of combo.slots) {
    const selections = slotSelections.get(slot.id) || [];
    
    if (selections.length === 0) {
      // If slot is fixed product type, add its price based on rule
      if (slot.slot_type === 'product' && slot.product) {
        if (slot.pricing_rule === 'fixed' && slot.fixed_price !== null) {
          totalPrice += slot.fixed_price;
        } else {
          totalPrice += slot.product.price;
        }
      }
      continue;
    }

    const prices = selections.map(s => s.price);

    switch (slot.pricing_rule) {
      case 'highest':
        totalPrice += Math.max(...prices);
        break;
      case 'average':
        totalPrice += prices.reduce((sum, p) => sum + p, 0) / prices.length;
        break;
      case 'fixed':
        totalPrice += (slot.fixed_price ?? 0) * selections.length;
        break;
      case 'rodizio':
        // Rodizio uses base combo price, not individual selections
        break;
    }
  }

  // If all slots are rodizio or no selections, use combo base price
  if (totalPrice === 0) {
    totalPrice = combo.price;
  }

  return totalPrice;
}
